/**
 * normalizeSiteName — 건설/관급 공사 현장명 띄어쓰기 규칙.
 *
 * ERP(ksms165 현장 마스터)는 현장명을 붙여쓰기로 저장한다. 원본은 read-only 라
 * 고칠 수 없으므로 표시 계층에서 정규화한다. 규칙이 못 잡는 건 site_aliases 수동 매핑.
 *
 * 규칙 근거는 실측이다 — tools/migration/site_name_audit.js 가 sales_sites 4,861건과
 * site_aliases 수동 교정본(정답지)을 대조한다. 규칙을 고치면 그 스크립트를 다시 돌려
 * 적중률이 올랐는지 확인할 것. 스크립트는 이 파일을 직접 트랜스파일해 쓴다.
 *
 * ── 동작 구조 ──────────────────────────────────────────────────────────
 * 규칙을 문자열에 순서대로 replace 하면 앞 규칙이 넣은 공백·placeholder 가 뒤 규칙의
 * 정규식을 눈멀게 한다("2025년묘량궁적천"에서 묘량을 먼저 보호하면 연도 규칙이 죽는다).
 * 그래서 문자열을 고치지 않는다 — 공백 없는 원문(bare)에 모든 규칙을 독립적으로 돌려
 * "여기 앞에서 끊는다(cut)" 위치만 모으고, 마지막에 한 번 조립한다.
 * 분리 금지 고유명사는 그 구간의 cut 을 막는(block) 방식으로 처리한다.
 *
 * ── 실측이 말해준 것 ────────────────────────────────────────────────────
 *   - 영광군 읍·면 지명이 접미사(읍/면) 없이 2,638회 등장한다("대마성산1리").
 *     정규식으로는 못 잡는다. 지명 사전이 있어야 한다.
 *   - "○○리" 패턴 1,464건. 옛 규칙(숫자+리)은 "계송리"를 못 잡았다.
 *   - 담당자의 실제 원칙은 "연도 / 지명 / 리 / 지구 단위로만 띄우고 공사명은 붙여 쓴다"
 *     이다("농로포장공사"는 붙임). 공사 접미사를 함부로 늘리면 과교정이 된다.
 */
import { DISTRICTS, TOWNSHIPS, TOWNSHIP_PARENTS, VILLAGES, VILLAGE_PARENTS } from "@/domain/districts.gen";

/** 분리 금지 합성 고유명사 — 이 구간 안에는 공백을 넣지 않는다 */
const PROTECTED_NOUNS = [
  "국토관리사무소",
  "지방국토관리청",
  "한국도로공사",
  "한국전력공사",
  "공업협동조합",
  "도로사업소",
  "농어촌공사",
  "광역도로",
  "도시계획",
  "제설분소",
  "관급자재",
  "사급자재",
];

/**
 * 읍·면 — ERP 는 "염산면"이 아니라 "염산"으로만 적는다. 앞뒤를 끊는다.
 * 목록은 법정동코드에서 생성한다(tools/migration/gen_district_dict.js).
 */
const ADMIN_PLACES = TOWNSHIPS;

/**
 * 시·군·구는 **행정지역명이 앞에 붙은 온전한 이름일 때만** 끊는다("영광군 백수").
 *
 * 접미사 글자만 보고 끊으면 "레미콘구입"(75건) "재해복구" "수리시설" "감시센터"가 쪼개진다
 * — sales_sites 4,861건 실측에서 "시"·"구"가 행정구역으로 쓰인 사례는 0건이었다(2026-09).
 * 사전 말고는 구분할 방법이 없다.
 */
const ADMIN_DISTRICTS = DISTRICTS;

/** 공사 종류 표현 — 앞에서 끊는다. 실측 근거 있는 것만 둔다(늘리면 과교정) */
const WORK_SUFFIXES = [
  "환경개선사업",
  "만들기사업",
  "정비공사",
  "개설공사",
  "보수공사",
  "확장공사",
  "신설공사",
  "신설사업",
  "조성사업",
  "정비사업",
  "기반사업",
];

/** 뒤에서 끊는 패턴 — 매치 끝 위치에 공백 */
const CUT_AFTER: RegExp[] = [
  /\d{4}년(?=[가-힣])/g,                    // 2025년간선임도
  /(?:\d+호선|국\d+)(?=[가-힣])/g,          // 국도77호선영광 / 국77영광
  /[가-힣]{2,3}지구(?=[가-힣])/g,           // 두우지구폭원부족
  // 도로명("청보리로4길")은 한 덩어리로 두고, 뒤에 붙은 건물번호만 뗀다
  /[가-힣]+로\d*길?(?=\d)/g,               // 청보리로4길57 · 군서로3길61
  // 읍·면만 접미사로 끊는다. 실측상 "○○읍/면"은 전부 실제 행정구역이었다.
  //   · 시·군·구 → ADMIN_DISTRICTS 사전이 담당(접미사만 보면 "구입"·"시설"을 쪼갠다)
  //   · 동 → 뺐다. "화물자동차"·"운동부"·"군남동편"을 쪼갠다
  //   · (?!리) 는 "마읍리"·"축동리"를 지킨다
  /[가-힣]+?[읍면](?=[가-힣])(?!리)/g,          // 영광읍입석리 · 염산면봉덕리
];

/**
 * 리(里) — 법정리 사전에서 만든 정규식. "성산리"뿐 아니라 "성산1리"처럼 숫자가 낀 표기도 잡는다.
 *
 * 정규식(`[가-힣]{2}리`)으로 하면 "중점관리지역"·"하수처리시설"의 "관리"·"처리"를
 * 마을로 오인한다. 사전이면 그 문제가 없다 — 대상 시군에 그런 이름의 리가 없기 때문이다.
 */
const VILLAGE_RE = new RegExp(
  `(?:${VILLAGES.map((v) => v.slice(0, -1)).join("|")})\\d*리`,
  "g",
);

/** offset 이 괄호 안인가 — 괄호 안 이름은 통째로 둔다 */
function inParens(bare: string, at: number): boolean {
  const open = bare.lastIndexOf("(", at);
  if (open === -1) return false;
  return bare.indexOf(")", open) > at || bare.indexOf(")", open) === -1;
}

/** 공백 없는 원문에서 term 이 나타나는 [start,end) 구간 전부 */
function occurrences(bare: string, term: string): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = bare.indexOf(term); i !== -1; i = bare.indexOf(term, i + 1)) {
    out.push([i, i + term.length]);
  }
  return out;
}

export function normalizeSiteName(raw: string): string {
  if (!raw || raw.length < 4) return raw;

  const bare = raw.replace(/\s+/g, "");
  if (bare.length < 4) return raw.trim();

  /** cut.has(i) === i 번째 글자 앞에서 끊는다 */
  const cut = new Set<number>();
  /** block.has(i) === i 번째 글자 앞은 절대 끊지 않는다(고유명사 내부) */
  const block = new Set<number>();

  // 원문에 이미 있던 공백은 그대로 존중한다
  {
    let i = 0;
    let prevSpace = false;
    for (const ch of raw) {
      if (/\s/.test(ch)) { prevSpace = true; continue; }
      if (prevSpace && i > 0) cut.add(i);
      prevSpace = false;
      i += 1;
    }
  }

  // 분리 금지 구간
  for (const noun of PROTECTED_NOUNS) {
    for (const [s, e] of occurrences(bare, noun)) {
      for (let i = s + 1; i < e; i += 1) block.add(i);
    }
  }

  // 읍·면 지명 — 앞뒤에서 끊고, 지명 안쪽은 막는다.
  //   안쪽을 막지 않으면 ADMIN_SUFFIX 가 "2026년군서"를 "년군"+"서"로 쪼갠다.
  //   괄호 안(예 "(영광교촌지구)")은 통째로 하나의 이름이라 분리하지 않는다.
  for (const place of ADMIN_PLACES) {
    for (const [s, e] of occurrences(bare, place)) {
      for (let i = s + 1; i < e; i += 1) block.add(i);
      if (inParens(bare, s)) continue;
      if (s > 0 && /[가-힣0-9]/.test(bare[s - 1])) cut.add(s);
      // 뒤에 붙은 글자가 행정 접미사(백수읍)거나 도로명(군서로)이면 끊지 않는다
      if (e < bare.length && /[가-힣]/.test(bare[e]) && !/[읍면군시로길]/.test(bare[e])) cut.add(e);
    }
  }

  // 시·군·구 — 사전에 있는 온전한 행정구역명 뒤에서만 끊는다
  for (const district of ADMIN_DISTRICTS) {
    for (const [ds, de] of occurrences(bare, district)) {
      for (let i = ds + 1; i < de; i += 1) block.add(i);
      if (ds > 0 && /[가-힣]/.test(bare[ds - 1])) cut.add(ds);
      if (de < bare.length && /[가-힣]/.test(bare[de])) cut.add(de);
    }
  }

  // 뒤에서 끊는 패턴
  for (const re of CUT_AFTER) {
    re.lastIndex = 0;
    for (const m of bare.matchAll(re)) {
      const end = m.index + m[0].length;
      if (end > 0 && end < bare.length) cut.add(end);
    }
  }

  // 리(里) — 법정리 사전 매칭. 이름 안쪽은 막아 다른 규칙이 쪼개지 못하게 한다
  VILLAGE_RE.lastIndex = 0;
  for (const m of bare.matchAll(VILLAGE_RE)) {
    const start = m.index;
    const end = start + m[0].length;
    for (let i = start + 1; i < end; i += 1) block.add(i);
    if (start > 0 && /[가-힣]/.test(bare[start - 1])) cut.add(start);
    if (end < bare.length) cut.add(end);
  }

  // 공사 종류 — 앞에서 끊는다(앞에 한글이 2자 이상 붙어 있을 때만)
  for (const suffix of WORK_SUFFIXES) {
    for (const [s] of occurrences(bare, suffix)) {
      if (s >= 2 && /[가-힣]/.test(bare[s - 1])) cut.add(s);
    }
  }

  // 괄호 — 앞뒤에서 끊는다
  for (let i = 0; i < bare.length; i += 1) {
    if (bare[i] === "(" && i > 0) cut.add(i);
    if (bare[i] === ")" && i + 1 < bare.length) cut.add(i + 1);
  }

  let out = "";
  for (let i = 0; i < bare.length; i += 1) {
    if (i > 0 && cut.has(i) && !block.has(i)) out += " ";
    out += bare[i];
  }
  return out.replace(/\s+/g, " ").trim();
}

/**
 * normalizeAddress — 주소 띄어쓰기 정리.
 *
 * 주소는 대부분 행정구역이라 현장명과 같은 사전·엔진이 그대로 맞는다
 * ("영광군백수읍상사리1087" → "영광군 백수읍 상사리 1087").
 * ERP 에는 주소가 없어서 우리가 모으는 값의 출처가 출하 비고(자유 텍스트)이고,
 * 거기 적힌 주소가 대개 붙여쓰기라 표시 시점에 정리한다(원본은 그대로 보존).
 */
export function normalizeAddress(raw: string): string {
  return normalizeSiteName(raw);
}

/**
 * completeAddress — 상위 행정구역이 빠진 주소를 법정동 사전으로 완성한다.
 *
 *   "송암리 680-7"        → "영광군 염산면 송암리 680-7"
 *   "염산 송암리 680-7"    → "영광군 염산면 송암리 680-7"
 *   "염산면 송암리 680-7"  → "영광군 염산면 송암리 680-7"
 *   "영광군 염산면 …"      → 그대로
 *
 * 설정 주소는 "명확한 주소"여야 한다. 비고에 적힌 "송암리 680-7"을 그대로 저장하면
 * 나중에 어느 송암리인지 알 수 없다. 같은 이름의 리가 여러 읍면에 있으면(모호) 손대지
 * 않는다 — 그 경우는 지오코딩이 돌려준 정규 주소가 최종이다.
 */
export function completeAddress(raw: string): string {
  const s = normalizeAddress(raw);
  if (!s) return s;
  const tokens = s.split(" ");

  // 이미 시군이 앞에 있으면 완성된 주소로 본다
  if (DISTRICTS.some((d) => tokens[0] === d || tokens[0].endsWith(d))) return s;
  if (/^(전라남도|전라북도|전남|전북|광주광역시|광주)$/.test(tokens[0])) return s;

  // ① 읍면(접미사 포함)으로 시작 — 시군만 붙인다
  if (/[읍면]$/.test(tokens[0]) && TOWNSHIP_PARENTS[tokens[0]]) {
    return `${TOWNSHIP_PARENTS[tokens[0]]} ${s}`;
  }
  // ② 읍면(접미사 없이 "염산")으로 시작 — 읍면 사전에서 접미사·시군을 복원한다.
  //    리가 동명이라 모호해도 읍면이 있으면 확정된다("염산 송암리" → "영광군 염산면 송암리")
  if (TOWNSHIPS.includes(tokens[0])) {
    const full = Object.keys(TOWNSHIP_PARENTS).find((t) => t.slice(0, -1) === tokens[0]);
    if (full) return `${TOWNSHIP_PARENTS[full]} ${full} ${tokens.slice(1).join(" ")}`.trim();
  }
  // ③ 리로 시작 — 시군·읍면을 붙인다
  const ri = stripRiNo(tokens[0]);
  if (VILLAGE_PARENTS[ri]) return `${VILLAGE_PARENTS[ri]} ${s}`;

  return s;
}

/** "성산1리" → "성산리" (사전 키는 숫자 없는 형태) */
function stripRiNo(token: string): string {
  return token.replace(/\d+리$/, "리");
}

/** 현장 코드가 붙은 행 — 출하(RqcShipment)·시뮬레이션 행이 모두 이 모양이다 */
export interface SiteKeyed {
  site: string;
  scode?: string;
  hcode?: string;
}

/**
 * displaySiteName — 확정 표시명 > 규칙 > 원본 순으로 폴백.
 *
 * key 는 `${scode}_${hcode}` (site_meta doc ID). 코드를 모르는 자리에서는 생략해도 되고,
 * 그때는 규칙 결과가 나온다.
 */
export function displaySiteName(
  raw: string,
  metas?: Map<string, { display?: string }>,
  key?: string | null,
): string {
  if (!raw) return "—";
  if (key && metas) {
    const hit = metas.get(key);
    if (hit?.display) return hit.display;
  }
  return normalizeSiteName(raw);
}

/**
 * siteNameOf — 출하 행에서 바로 표시명을 뽑는다 (호출부에서 키 조립 생략).
 */
export function siteNameOf(
  row: SiteKeyed,
  metas?: Map<string, { display?: string }>,
): string {
  const s = String(row.scode ?? "").trim();
  const h = String(row.hcode ?? "").trim();
  return displaySiteName(row.site, metas, s && h ? `${s}_${h}` : null);
}
